// app/javascript/template_combiner.js


window.CombineApp = {
  selectedTemplates: new Set(),
  combineInProgress: false,

  // Stable named handlers so removeEventListener works symmetrically
  handleCheckboxChange(e) {
    if (e.target && e.target.classList.contains('combine-checkbox')) {
      window.CombineApp.toggleSelection(e.target);
    }
  },

  handleCardClick(e) {
    if (e.target && e.target.classList.contains('combine-checkbox')) {
      e.stopPropagation();
    }
  },

  init() {
    if (!document.getElementById('combine-modal')) return;

    this.selectedTemplates.clear();
    this.combineInProgress = false;

    // Remove before re-adding — prevents stacked listeners on Turbo navigations
    document.removeEventListener('change', this.handleCheckboxChange);
    document.removeEventListener('click',  this.handleCardClick);
    document.addEventListener('change', this.handleCheckboxChange);
    document.addEventListener('click',  this.handleCardClick);

    // Clear data-bound flags so bindModalEvents re-attaches after Turbo swaps DOM
    this.clearBoundFlags();
    this.bindModalEvents();
    this.resetCheckboxes();
    this.updateFloatingButton();

    // Same fix as back button: inject turbo-cache-control to prevent stale CSP page
    if (!document.querySelector('meta[name="turbo-cache-control"]')) {
      const meta = document.createElement('meta');
      meta.name    = 'turbo-cache-control';
      meta.content = 'no-cache';
      document.head.appendChild(meta);
    }
  },

  clearBoundFlags() {
    ['combine-templates-btn', 'combine-modal', 'perform-combine-btn', 'folder-select']
      .forEach(id => {
        const el = document.getElementById(id);
        if (el) delete el.dataset.combineListenerBound;
      });
    document.querySelectorAll('.close-modal').forEach(btn => {
      delete btn.dataset.combineListenerBound;
    });
  },

  bindModalEvents() {
    const openBtn = document.getElementById('combine-templates-btn');
    if (openBtn && openBtn.dataset.combineListenerBound !== 'true') {
      openBtn.addEventListener('click', () => window.CombineApp.openModal());
      openBtn.dataset.combineListenerBound = 'true';
    }

    document.querySelectorAll('.close-modal').forEach(btn => {
      if (btn.dataset.combineListenerBound !== 'true') {
        btn.addEventListener('click', () => window.CombineApp.closeModal());
        btn.dataset.combineListenerBound = 'true';
      }
    });

    const modal = document.getElementById('combine-modal');
    if (modal && modal.dataset.combineListenerBound !== 'true') {
      modal.addEventListener('click', (e) => {
        if (e.target.id === 'combine-modal') window.CombineApp.closeModal();
      });
      modal.dataset.combineListenerBound = 'true';
    }

    const performBtn = document.getElementById('perform-combine-btn');
    if (performBtn && performBtn.dataset.combineListenerBound !== 'true') {
      performBtn.addEventListener('click', () => window.CombineApp.performCombine());
      performBtn.dataset.combineListenerBound = 'true';
    }

    const folderSelect   = document.getElementById('folder-select');
    const newFolderInput = document.getElementById('new-folder-input');
    if (folderSelect && newFolderInput && folderSelect.dataset.combineListenerBound !== 'true') {
      folderSelect.addEventListener('change', (e) => {
        if (e.target.value === '__new__') {
          newFolderInput.classList.remove('hidden');
          newFolderInput.focus();
        } else {
          newFolderInput.classList.add('hidden');
          newFolderInput.value = '';
        }
      });
      folderSelect.dataset.combineListenerBound = 'true';
    }
  },

  resetCheckboxes() {
    document.querySelectorAll('.combine-checkbox:checked').forEach(cb => {
      cb.checked = false;
    });
  },

  toggleSelection(checkbox) {
    const templateId = checkbox.dataset.templateId;
    if (checkbox.checked) {
      this.selectedTemplates.add(templateId);
    } else {
      this.selectedTemplates.delete(templateId);
    }
    this.updateFloatingButton();
  },

  updateFloatingButton() {
    const btn = document.getElementById('combine-templates-btn');
    if (!btn) return;

    const count = this.selectedTemplates.size;
    if (count >= 2) {
      btn.classList.remove('hidden');
      const countSpan = btn.querySelector('.count');
      if (countSpan) countSpan.textContent = count;
    } else {
      btn.classList.add('hidden');
    }
  },

  openModal() {
    const modal = document.getElementById('combine-modal');
    if (!modal) return;
    modal.classList.remove('hidden');

    const folderSelect   = document.getElementById('folder-select');
    const newFolderInput = document.getElementById('new-folder-input');
    if (folderSelect)   folderSelect.value = '';
    if (newFolderInput) { newFolderInput.value = ''; newFolderInput.classList.add('hidden'); }

    this.loadTemplatePreview();
  },

  closeModal() {
    const modal  = document.getElementById('combine-modal');
    const status = document.getElementById('combine-status');
    if (modal)  modal.classList.add('hidden');
    if (status) status.innerHTML = '';
  },

  loadTemplatePreview() {
    const container = document.getElementById('templates-preview-list');
    if (!container) return;

    container.innerHTML = '<div class="text-center py-4">Loading...</div>';
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content;

    fetch('/templates/batch_info', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body:    JSON.stringify({ template_ids: Array.from(this.selectedTemplates) })
    })
      .then(r => r.json())
      .then(data => {
        if (data.templates) {
          this.renderTemplatesList(data.templates);
        } else {
          container.innerHTML = '<div class="text-red-500">Failed to load templates</div>';
        }
      })
      .catch(err => {
        console.error('CombineApp fetch error:', err);
        container.innerHTML = '<div class="text-red-500">Error loading templates.</div>';
      });
  },

  renderTemplatesList(templates) {
    const container = document.getElementById('templates-preview-list');
    if (!container) return;

    container.innerHTML = templates.map((t, index) => `
      <div class="template-preview-item bg-white p-4 border rounded-lg shadow-sm mb-2 cursor-move transition-all duration-200"
           draggable="true" data-template-id="${t.id}">
        <div class="flex items-center justify-between pointer-events-none">
          <div class="flex items-center space-x-3 flex-1">
            <span class="item-index font-bold text-gray-600 text-lg">${index + 1}.</span>
            <div class="flex-1">
              <h4 class="font-semibold text-gray-900">${this.escapeHtml(t.name)}</h4>
              <div class="text-sm text-gray-600 mt-1">${t.pages_count || 0} pages</div>
            </div>
          </div>
          <button type="button"
                  class="remove-template pointer-events-auto text-red-500 hover:text-red-700 font-bold text-xl px-2"
                  data-template-id="${t.id}">×</button>
        </div>
      </div>
    `).join('');

    container.querySelectorAll('.remove-template').forEach(btn => {
      btn.addEventListener('click', (e) => {
        window.CombineApp.removeTemplate(e.target.dataset.templateId);
      });
    });

    this.initDragAndDrop(container);
  },

  initDragAndDrop(container) {
    container.querySelectorAll('.template-preview-item').forEach(draggable => {
      draggable.addEventListener('dragstart', () => draggable.classList.add('dragging', 'opacity-50'));
      draggable.addEventListener('dragend',   () => {
        draggable.classList.remove('dragging', 'opacity-50');
        window.CombineApp.updateOrderNumbers(container);
      });
    });

    container.addEventListener('dragover', (e) => {
      e.preventDefault();
      const after    = window.CombineApp.getDragAfterElement(container, e.clientY);
      const dragging = container.querySelector('.dragging');
      if (dragging) {
        after == null ? container.appendChild(dragging) : container.insertBefore(dragging, after);
      }
    });
  },

  getDragAfterElement(container, y) {
    return [...container.querySelectorAll('.template-preview-item:not(.dragging)')]
      .reduce((closest, child) => {
        const offset = y - child.getBoundingClientRect().top - child.getBoundingClientRect().height / 2;
        return (offset < 0 && offset > closest.offset) ? { offset, element: child } : closest;
      }, { offset: Number.NEGATIVE_INFINITY }).element;
  },

  updateOrderNumbers(container) {
    container.querySelectorAll('.template-preview-item').forEach((item, index) => {
      const span = item.querySelector('.item-index');
      if (span) span.textContent = `${index + 1}.`;
    });
  },

  removeTemplate(templateId) {
    this.selectedTemplates.delete(templateId);
    const checkbox = document.querySelector(`.combine-checkbox[data-template-id="${templateId}"]`);
    if (checkbox) checkbox.checked = false;
    this.updateFloatingButton();
    this.selectedTemplates.size >= 2 ? this.loadTemplatePreview() : this.closeModal();
  },

  performCombine() {
    if (this.combineInProgress) return;

    const nameInput      = document.getElementById('combined-template-name');
    const mergeCheck     = document.getElementById('combine-merge-submitters');
    const folderSelect   = document.getElementById('folder-select');
    const newFolderInput = document.getElementById('new-folder-input');
    const performBtn     = document.getElementById('perform-combine-btn');
    const status         = document.getElementById('combine-status');

    const name = nameInput ? nameInput.value.trim() : '';
    if (!name) { alert('Please enter a name'); return; }

    let folder = '';
    if (folderSelect?.value === '__new__' && newFolderInput) {
      folder = newFolderInput.value.trim();
    } else if (folderSelect?.value) {
      folder = folderSelect.value.trim();
    }

    this.combineInProgress = true;
    const originalText = performBtn?.textContent || '';
    if (performBtn) { performBtn.disabled = true; performBtn.textContent = 'Combining...'; }
    if (status)     status.innerHTML = '<div class="text-center py-4 text-blue-600">Combining templates...</div>';

    const orderedIds = [...document.querySelectorAll('.template-preview-item')]
      .map(el => el.dataset.templateId);
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content;

    fetch('/templates/combine', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body:    JSON.stringify({
        template_ids:     orderedIds,
        name,
        folder_name:      folder,
        merge_submitters: mergeCheck ? mergeCheck.checked : true
      })
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          if (status) status.innerHTML = '<div class="text-green-600 text-center">Success! Redirecting...</div>';
          const url = data.redirect_url || `/templates/${data.template_id}/edit`;
          window.Turbo ? window.Turbo.visit(url) : (window.location.href = url);
        } else {
          throw new Error(data.error || 'Unknown error');
        }
      })
      .catch(err => {
        this.combineInProgress = false;
        if (performBtn) { performBtn.disabled = false; performBtn.textContent = originalText; }
        if (status) status.innerHTML = `<div class="text-red-600 text-center">${this.escapeHtml(err.message || 'Error')}</div>`;
      });
  },

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
};

function initCombineApp() {
  window.CombineApp.init();
}

document.removeEventListener('turbo:load',   initCombineApp);
document.removeEventListener('turbo:render', initCombineApp);
document.addEventListener('turbo:load',   initCombineApp);
document.addEventListener('turbo:render', initCombineApp);

// Fallback: module may run after DOMContentLoaded has already fired
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCombineApp);
} else {
  initCombineApp();
}
